import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { api, ApiError, type ModeloRetorno, type RetornoEntrevistaResposta } from "../api";
import type { Row } from "../types";
import { IconChat, IconCheck, IconClose, IconCopy, IconEdit, IconUser } from "../icons";
import { abrirJanela } from "../endomarketing-utils";
import {
  MARCADORES,
  MAX_MENSAGEM_RETORNO,
  MAX_MODELO_RETORNO,
  ROTULO_MARCADOR,
  ROTULO_TIPO,
  TIPOS_RETORNO,
  aplicarModelo,
  avisoDeStatus,
  formatarTelefone,
  linkWhatsAppRetorno,
  marcadoresDoTexto,
  marcadoresPendentes,
  numeroWhatsApp,
  telefoneValido,
  tipoPorStatus,
  type Marcador,
  type TipoRetorno,
} from "../retorno-entrevista";

/**
 * RETORNO AO CANDIDATO — o resultado do processo seletivo pelo WhatsApp.
 *
 * O QUE ISTO RESOLVE
 * ------------------
 * Terminada a entrevista, o RH avisava o candidato à mão: cada um escrevendo
 * de um jeito e o aprovado recebendo a proposta (salário, horário, benefícios)
 * montada na hora. Pedido de 22/10/2026: mandar o retorno, positivo ou
 * negativo, "pelo site da SERCAMP para o WhatsApp dos candidatos", com os dois
 * modelos que o RH/DP passou (src/retorno-entrevista.ts).
 *
 * COMO A TELA FUNCIONA
 *  - O tipo já vem escolhido pelo status (Aprovado… → positivo, Reprovado →
 *    negativo); nos outros status o RH escolhe.
 *  - O texto sai do MODELO com os campos (nome, vaga, salário, horário). O que
 *    o RH ainda não preencheu fica como marcador no texto ("{salario}") — é a
 *    prévia mostrando o que falta — e o envio só libera quando não sobrar
 *    nenhum. Salário e horário são de propósito em branco: o RH põe antes de
 *    mandar, a cada candidato.
 *  - O texto é editável. Mexeu à mão, os campos param de reescrevê-lo (senão a
 *    próxima tecla num campo apagaria a correção) e aparece "Refazer o texto
 *    pelos campos" para voltar ao modelo.
 *  - "Enviar pelo WhatsApp" abre a conversa com o texto pronto — o RH aperta
 *    enviar. A janela abre DENTRO do clique, antes de qualquer espera: depois
 *    de um `await` o bloqueador de pop-up do navegador barra. As perguntas
 *    ("o status diz Reprovado…", "já recebeu retorno…") são `confirm`, que é
 *    síncrono, pelo mesmo motivo.
 *  - Abrir o WhatsApp já registra o envio no histórico (mesma decisão do
 *    convite: ninguém volta para clicar em "marcar como enviado"). Quem mandou
 *    por fora (copiou e colou no celular) usa "Registrar como enviado".
 *  - "Editar o modelo" ajusta o texto para os PRÓXIMOS retornos, sem esperar
 *    atualização do sistema (fica em rh_config, no servidor).
 */

type Campos = { telefone: string; nome: string; vaga: string; salario: string; horario: string };

const CAMPOS_VAZIOS: Campos = { telefone: "", nome: "", vaga: "", salario: "", horario: "" };

type DadosEnvio = { tipo: TipoRetorno; telefone: string; mensagem: string };

const DESCRICAO_TIPO: Record<TipoRetorno, string> = {
  positivo: "Aprovado(a) — vai com a proposta: vaga, salário, horário e benefícios",
  negativo: "Agradecimento — o processo seguiu com outro profissional",
};

const ORIGEM_TELEFONE: Record<"retorno" | "triagem" | "disc", string> = {
  retorno: "O mesmo número do último retorno.",
  triagem: "Telefone da triagem do currículo.",
  disc: "Telefone deixado no teste DISC.",
};

/** O que cada campo pede (o exemplo que aparece vazio). */
const EXEMPLO: Record<Marcador, string> = {
  nome: "Primeiro nome",
  vaga: "Ex.: Eletricista de Manutenção",
  salario: "Ex.: R$ 2.800,00",
  horario: "Ex.: segunda a sexta, das 7h às 17h",
};

const ROTULO_CAMPO: Record<Marcador, string> = {
  nome: "Nome no texto",
  vaga: "Vaga",
  salario: "Salário",
  horario: "Horário",
};

/** Carimbo de data e hora (o que o servidor grava em ISO). */
function quando(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

const tipoEmMinusculas = (t: TipoRetorno) => ROTULO_TIPO[t].toLowerCase();

/**
 * Copia para a área de transferência; devolve se realmente conseguiu. O
 * caminho antigo (textarea + execCommand) fica porque `navigator.clipboard` não
 * funciona em http fora do localhost — que é como o RH abre o sistema pela
 * rede interna (mesma técnica do convite e do clima).
 */
async function copiarTexto(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = texto;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

/**
 * Linha do bloco de resumo. FORA do modal de propósito: componente declarado
 * dentro de outro é recriado a cada render do pai e os campos perdem o cursor.
 */
function LinhaResumo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="convite-resumo-linha">
      <span>{rotulo}</span>
      <strong>{children}</strong>
    </div>
  );
}

/**
 * "Editar o modelo" — o texto que vale para os próximos retornos. Fora do
 * modal pelo mesmo motivo do LinhaResumo (o cursor do textarea).
 */
function EditorModelo({
  tipo,
  modelo,
  onSalvar,
  onRestaurar,
  onCancelar,
}: {
  tipo: TipoRetorno;
  modelo: ModeloRetorno;
  onSalvar: (texto: string) => Promise<void>;
  onRestaurar: () => Promise<void>;
  onCancelar: () => void;
}) {
  const [texto, setTexto] = useState(modelo.texto);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);

  /** Põe o marcador onde está o cursor (ou no lugar do trecho selecionado). */
  function inserir(chave: Marcador) {
    const marcador = `{${chave}}`;
    const el = ref.current;
    const ini = el?.selectionStart ?? texto.length;
    const fim = el?.selectionEnd ?? texto.length;
    setTexto(texto.slice(0, ini) + marcador + texto.slice(fim));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(ini + marcador.length, ini + marcador.length);
    });
  }

  async function executar(acao: () => Promise<void>) {
    setSalvando(true);
    setErro(null);
    try {
      await acao();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui salvar o modelo.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="retorno-editor">
      <p className="modal-sub">
        Modelo do <strong>{tipoEmMinusculas(tipo)}</strong> — vale para os próximos retornos de todas as
        entrevistas. Clique num marcador para colocá-lo onde está o cursor: na hora de mandar, ele vira o dado
        do candidato.
      </p>
      <div className="endo-chips retorno-marcadores">
        {MARCADORES.map((m) => (
          <button
            key={m.chave}
            type="button"
            className="endo-chip"
            title={`Vira o ${m.rotulo} de cada candidato`}
            onClick={() => inserir(m.chave)}
          >
            {`{${m.chave}}`}
          </button>
        ))}
      </div>
      <textarea
        ref={ref}
        className="convite-preview-texto retorno-texto"
        rows={14}
        maxLength={MAX_MODELO_RETORNO}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        aria-label={`Modelo do ${tipoEmMinusculas(tipo)}`}
      />
      {erro && <div className="form-error">{erro}</div>}
      <div className="convite-acoes">
        <button
          type="button"
          className="btn-primary btn-sm"
          data-grava
          disabled={salvando || !texto.trim()}
          onClick={() => executar(() => onSalvar(texto))}
        >
          {salvando ? "Salvando…" : "Salvar modelo"}
        </button>
        {modelo.personalizado && (
          <button
            type="button"
            className="btn-secondary btn-sm"
            data-grava
            disabled={salvando}
            onClick={() => {
              if (!confirm(`Voltar o ${tipoEmMinusculas(tipo)} ao texto original da SERCAMP?`)) return;
              void executar(onRestaurar);
            }}
          >
            Voltar ao texto original
          </button>
        )}
        <button type="button" className="btn-secondary btn-sm" onClick={onCancelar} disabled={salvando}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

export default function RetornoEntrevistaModal({
  entrevista,
  onClose,
  onMudou,
}: {
  entrevista: Row;
  onClose: () => void;
  /** Avisa a tela de trás (aviso no topo da listagem). NÃO fecha o modal. */
  onMudou?: (mensagem: string) => void;
}) {
  const entrevistaId = Number(entrevista.id);

  const [dados, setDados] = useState<RetornoEntrevistaResposta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [tipo, setTipo] = useState<TipoRetorno | null>(null);
  const [campos, setCampos] = useState<Campos>(CAMPOS_VAZIOS);
  /** null = o texto sai do modelo com os campos; texto = o RH editou à mão. */
  const [textoManual, setTextoManual] = useState<string | null>(null);

  const [registrando, setRegistrando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [editandoModelo, setEditandoModelo] = useState(false);
  /** O navegador barrou a janela: o link fica na tela para abrir com um clique. */
  const [barrado, setBarrado] = useState<{ link: string; envio: DadosEnvio } | null>(null);
  /**
   * O retorno das ações (erro, "registrado", janela barrada) fica junto dos
   * botões, lá embaixo — no topo ele aparecia fora da vista de quem acabou de
   * clicar em "Enviar", e o clique parecia não ter feito nada.
   */
  const avisoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /**
   * Relê do servidor. `manterFormulario`: depois de registrar um envio só o
   * HISTÓRICO muda — recarregar os campos junto apagaria o salário digitado e
   * o texto corrigido à mão.
   */
  const carregar = useCallback(
    async (manterFormulario = false) => {
      setCarregando(true);
      setErro(null);
      try {
        const r = await api.retornoEntrevista.get(entrevistaId);
        setDados(r);
        if (!manterFormulario) {
          setTipo(tipoPorStatus(r.entrevista.status));
          setCampos({
            ...CAMPOS_VAZIOS,
            telefone: formatarTelefone(r.sugestao.telefone),
            nome: r.sugestao.nome,
            vaga: r.sugestao.vaga,
          });
          setTextoManual(null);
        }
      } catch (e) {
        setErro(e instanceof ApiError ? e.message : "Não consegui carregar o retorno.");
      } finally {
        setCarregando(false);
      }
    },
    [entrevistaId],
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (erro || aviso || barrado) avisoRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [erro, aviso, barrado]);

  const modelo = tipo && dados ? dados.modelos[tipo] : null;
  /** Os campos que este modelo usa — só eles aparecem (e são obrigatórios). */
  const usados = useMemo(() => marcadoresDoTexto(modelo?.texto ?? ""), [modelo]);
  const textoGerado = useMemo(() => (modelo ? aplicarModelo(modelo.texto, campos) : ""), [modelo, campos]);
  const texto = textoManual ?? textoGerado;
  const faltam = marcadoresPendentes(texto);

  const envios = dados?.envios ?? [];
  const ultimo = envios[0] ?? null;
  const status = String(dados?.entrevista.status ?? entrevista.status ?? "");
  const conflito = tipo ? avisoDeStatus(tipo, status) : null;
  const numero = numeroWhatsApp(campos.telefone);
  const temFone = campos.telefone.replace(/\D/g, "") !== "";
  const foneIncompleto = temFone && !telefoneValido(numero);

  function alterar(campo: keyof Campos, valor: string) {
    setCampos((atuais) => ({ ...atuais, [campo]: valor }));
    setErro(null);
  }

  function escolherTipo(t: TipoRetorno) {
    if (t === tipo) return;
    if (
      textoManual !== null &&
      !confirm("Trocar o tipo de retorno descarta o texto que você editou à mão. Trocar mesmo assim?")
    ) {
      return;
    }
    setTipo(t);
    setTextoManual(null);
    setEditandoModelo(false);
    setBarrado(null);
    setErro(null);
    setAviso(null);
  }

  /** O que impede o envio (null = pode mandar). */
  function conferir(): string | null {
    if (!tipo) return "Escolha se o retorno é positivo ou negativo.";
    const mensagem = texto.trim();
    if (!mensagem) return "A mensagem está vazia.";
    if (faltam.length > 0) {
      return `Preencha antes de enviar: ${faltam.map((m) => ROTULO_MARCADOR[m]).join(", ")}.`;
    }
    if (mensagem.length > MAX_MENSAGEM_RETORNO) {
      return `A mensagem passou de ${MAX_MENSAGEM_RETORNO} caracteres — encurte o texto.`;
    }
    if (foneIncompleto) {
      return "O telefone do candidato está incompleto — informe o DDD e o número, ex.: (19) 99999-9999.";
    }
    return null;
  }

  /**
   * As perguntas antes de mandar. SÍNCRONAS de propósito: o WhatsApp tem de
   * abrir ainda dentro do clique (ver o cabeçalho).
   */
  function confirmar(t: TipoRetorno, abrindoWhatsApp: boolean): boolean {
    if (conflito && !confirm(`${conflito}\n\nMandar mesmo assim?`)) return false;
    if (ultimo) {
      const por = ultimo.enviado_por ? ` (por ${ultimo.enviado_por})` : "";
      const pergunta =
        ultimo.tipo === t ? "Mandar de novo?" : `Mandar agora o ${tipoEmMinusculas(t)}?`;
      if (
        !confirm(
          `Este candidato já recebeu o ${tipoEmMinusculas(ultimo.tipo)} em ${quando(ultimo.enviado_em)}${por}.\n\n${pergunta}`,
        )
      ) {
        return false;
      }
    }
    if (
      abrindoWhatsApp &&
      !numero &&
      !confirm("Sem o telefone do candidato, o WhatsApp abre para você escolher a conversa. Continuar?")
    ) {
      return false;
    }
    return true;
  }

  async function registrar(envio: DadosEnvio, peloWhatsApp: boolean) {
    setRegistrando(true);
    try {
      const r = await api.retornoEntrevista.registrar(entrevistaId, envio);
      setAviso(r.mensagem);
      onMudou?.(r.mensagem);
      await carregar(true);
    } catch (e) {
      const motivo = e instanceof ApiError ? e.message : "falha de conexão";
      setErro(
        peloWhatsApp
          ? `O WhatsApp abriu, mas o envio não ficou no histórico (${motivo}). Depois de mandar, use "Registrar como enviado".`
          : `Não consegui registrar o envio: ${motivo}`,
      );
    } finally {
      setRegistrando(false);
    }
  }

  function enviarPeloWhatsApp() {
    setErro(null);
    setAviso(null);
    setBarrado(null);
    const problema = conferir();
    if (problema || !tipo) {
      setErro(problema);
      return;
    }
    if (!confirmar(tipo, true)) return;
    const envio: DadosEnvio = { tipo, telefone: numero, mensagem: texto.trim() };
    const link = linkWhatsAppRetorno(numero, envio.mensagem);
    if (abrirJanela(link)) void registrar(envio, true);
    else setBarrado({ link, envio });
  }

  /** Mandou por fora (copiou e colou no celular): só registra no histórico. */
  function registrarSemAbrir() {
    setErro(null);
    setAviso(null);
    setBarrado(null);
    const problema = conferir();
    if (problema || !tipo) {
      setErro(problema);
      return;
    }
    if (!confirmar(tipo, false)) return;
    void registrar({ tipo, telefone: numero, mensagem: texto.trim() }, false);
  }

  async function copiar() {
    const ok = await copiarTexto(texto.trim());
    if (!ok) {
      setErro("Não consegui copiar sozinho — selecione o texto e copie.");
      return;
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  }

  /** Depois de trocar o modelo: o texto editado à mão não muda sozinho — avisa. */
  function aposTrocarModelo(mensagem: string, modelos: RetornoEntrevistaResposta["modelos"]) {
    setDados((atuais) => (atuais ? { ...atuais, modelos } : atuais));
    setEditandoModelo(false);
    setAviso(
      textoManual !== null
        ? `${mensagem} Esta mensagem foi editada à mão e não mudou — use "Refazer o texto pelos campos" para aplicar o modelo novo.`
        : mensagem,
    );
  }

  async function salvarModelo(novo: string) {
    if (!tipo) return;
    const r = await api.retornoEntrevista.salvarModelo(tipo, novo);
    aposTrocarModelo(r.mensagem, r.modelos);
  }

  async function restaurarModelo() {
    if (!tipo) return;
    const r = await api.retornoEntrevista.restaurarModelo(tipo);
    aposTrocarModelo(r.mensagem, r.modelos);
  }

  const daEntrevista = dados?.entrevista ?? null;
  const candidato = dados?.candidato ?? null;
  const origemFone = dados?.sugestao.telefone_origem ?? null;

  /** Erro, confirmação e janela barrada — perto dos botões quando há mensagem (ver avisoRef). */
  const retornoDasAcoes = (erro || aviso || barrado) && (
    <div ref={avisoRef} className="retorno-feedback">
      {erro && <div className="form-error">{erro}</div>}
      {aviso && !erro && <div className="form-ok">{aviso}</div>}
      {barrado && (
        <div className="convite-aviso retorno-barrado">
          <span>O navegador bloqueou a janela do WhatsApp.</span>
          <a
            className="btn-primary btn-sm"
            href={barrado.link}
            target="_blank"
            rel="noreferrer"
            data-grava
            onClick={() => {
              const { envio } = barrado;
              setBarrado(null);
              void registrar(envio, true);
            }}
          >
            <IconChat size={15} /> Abrir o WhatsApp
          </a>
        </div>
      )}
    </div>
  );
  /** "fulano, 22/10/2026 14:30" — quem ajustou o modelo e quando. */
  const autoriaModelo = modelo
    ? [modelo.atualizado_por, modelo.atualizado_em ? quando(modelo.atualizado_em) : null].filter(Boolean).join(", ")
    : "";

  return createPortal(
    // Pode abrir por cima de outro modal da aba Entrevistas: sem o
    // stopPropagation, o clique aqui dentro subiria até o overlay de baixo.
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="modal-card convite-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>
              <IconChat size={18} /> Retorno ao candidato
            </h3>
            <p className="modal-sub">O resultado do processo seletivo, pronto para mandar pelo WhatsApp</p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          {carregando && !dados ? (
            <p className="empty">Carregando o retorno…</p>
          ) : !dados ? (
            <div className="form-error">{erro ?? "Não consegui carregar o retorno."}</div>
          ) : (
            <>
              {/* ---------------- De quem é ---------------- */}
              <div className="convite-resumo">
                <LinhaResumo rotulo="Candidato">
                  <IconUser size={14} /> {String(daEntrevista?.candidato ?? "—")}
                </LinhaResumo>
                <LinhaResumo rotulo="Vaga / cargo">
                  {dados.vaga
                    ? `${dados.vaga}${daEntrevista?.cargo ? ` · ${daEntrevista.cargo}` : ""}`
                    : String(daEntrevista?.cargo || "—")}
                </LinhaResumo>
                <LinhaResumo rotulo="Status da entrevista">{status || "—"}</LinhaResumo>
                {(candidato?.telefone || candidato?.email) && (
                  <LinhaResumo rotulo="Contato do candidato">
                    {[candidato?.telefone, candidato?.email].filter(Boolean).join(" · ")}
                  </LinhaResumo>
                )}
              </div>

              {ultimo && (
                <div className="convite-aviso">
                  <strong>Este candidato já recebeu retorno.</strong> O último foi o{" "}
                  {tipoEmMinusculas(ultimo.tipo)}, em {quando(ultimo.enviado_em)}
                  {ultimo.enviado_por ? ` (por ${ultimo.enviado_por})` : ""}. Confira o histórico no fim
                  desta tela antes de mandar de novo.
                </div>
              )}

              {!(tipo && modelo) && retornoDasAcoes}

              {/* ---------------- Positivo ou negativo ---------------- */}
              <section className="convite-secao">
                <h4 className="convite-secao-titulo">Qual retorno</h4>
                <div className="retorno-tipos" role="radiogroup" aria-label="Tipo de retorno">
                  {TIPOS_RETORNO.map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={tipo === t}
                      className={`retorno-tipo ${tipo === t ? "on" : ""}`}
                      onClick={() => escolherTipo(t)}
                    >
                      <strong>
                        {tipo === t && <IconCheck size={15} />} {ROTULO_TIPO[t]}
                      </strong>
                      <small>{DESCRICAO_TIPO[t]}</small>
                    </button>
                  ))}
                </div>
                {!tipo && (
                  <p className="muted">
                    Escolha o retorno{status ? ` — o status "${status}" não diz sozinho qual mandar` : ""}.
                  </p>
                )}
                {conflito && (
                  <div className="convite-aviso retorno-conflito">
                    {conflito} Confira antes de mandar.
                  </div>
                )}
              </section>

              {tipo && modelo && (
                <>
                  {/* ---------------- Para quem e o que entra no texto ---------------- */}
                  <section className="convite-secao">
                    <h4 className="convite-secao-titulo">Para quem e o que entra no texto</h4>
                    <div className="convite-grid">
                      <label className="field">
                        <span>WhatsApp do candidato</span>
                        <input
                          type="tel"
                          inputMode="tel"
                          value={campos.telefone}
                          onChange={(e) => alterar("telefone", e.target.value)}
                          placeholder="(19) 99999-9999"
                        />
                        <small className={`retorno-dica ${foneIncompleto ? "retorno-dica-alerta" : ""}`}>
                          {foneIncompleto
                            ? "Número incompleto — falta o DDD?"
                            : !temFone
                              ? "Sem telefone: o WhatsApp abre para você escolher a conversa."
                              : origemFone && numero === numeroWhatsApp(dados.sugestao.telefone)
                                ? ORIGEM_TELEFONE[origemFone]
                                : "Número digitado aqui."}
                        </small>
                      </label>
                      {usados.map((m) => (
                        <label key={m} className="field">
                          <span>
                            {ROTULO_CAMPO[m]} <span className="req">*</span>
                          </span>
                          <input
                            value={campos[m]}
                            onChange={(e) => alterar(m, e.target.value)}
                            placeholder={EXEMPLO[m]}
                          />
                        </label>
                      ))}
                    </div>
                  </section>

                  {/* ---------------- A mensagem ---------------- */}
                  <section className="convite-secao">
                    <h4 className="convite-secao-titulo">Mensagem — confira antes de mandar</h4>
                    {textoManual !== null && (
                      <p className="retorno-manual">
                        Você mudou o texto à mão — os campos acima não mexem mais nele.{" "}
                        <button type="button" className="link-btn" onClick={() => setTextoManual(null)}>
                          Refazer o texto pelos campos
                        </button>
                      </p>
                    )}
                    <textarea
                      className="convite-preview-texto retorno-texto"
                      rows={14}
                      maxLength={MAX_MENSAGEM_RETORNO}
                      value={texto}
                      onChange={(e) => setTextoManual(e.target.value)}
                      aria-label="Mensagem do retorno"
                    />
                    {faltam.length > 0 && (
                      <p className="retorno-falta">
                        Falta preencher: <strong>{faltam.map((m) => ROTULO_MARCADOR[m]).join(", ")}</strong>. O
                        envio libera quando não sobrar nenhum marcador entre chaves no texto.
                      </p>
                    )}
                    <div className="convite-acoes">
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        data-grava
                        onClick={enviarPeloWhatsApp}
                        disabled={registrando}
                      >
                        <IconChat size={15} /> Enviar pelo WhatsApp
                      </button>
                      <button type="button" className="btn-secondary btn-sm" onClick={copiar}>
                        {copiado ? <IconCheck size={15} /> : <IconCopy size={15} />}{" "}
                        {copiado ? "Copiada!" : "Copiar mensagem"}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        data-grava
                        onClick={registrarSemAbrir}
                        disabled={registrando}
                        title="Mandou de outro jeito (copiou e colou no celular)? Registre aqui para ficar no histórico"
                      >
                        Registrar como enviado
                      </button>
                    </div>
                    {retornoDasAcoes}
                    <p className="muted">
                      Nada é enviado sozinho: o WhatsApp abre com este texto pronto e você aperta enviar. O
                      envio fica registrado no histórico desta entrevista.
                    </p>

                    <div className="retorno-modelo-linha">
                      <span className="muted">
                        {modelo.personalizado ? `Modelo ajustado pelo RH${autoriaModelo ? ` (${autoriaModelo})` : ""}.` : "Modelo padrão da SERCAMP."}
                      </span>
                      {!editandoModelo && (
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          data-grava
                          onClick={() => setEditandoModelo(true)}
                        >
                          <IconEdit size={14} /> Editar o modelo
                        </button>
                      )}
                    </div>
                    {editandoModelo && (
                      <EditorModelo
                        key={tipo}
                        tipo={tipo}
                        modelo={modelo}
                        onSalvar={salvarModelo}
                        onRestaurar={restaurarModelo}
                        onCancelar={() => setEditandoModelo(false)}
                      />
                    )}
                  </section>
                </>
              )}

              {/* ---------------- Histórico ---------------- */}
              {envios.length > 0 && (
                <section className="convite-secao">
                  <h4 className="convite-secao-titulo">Retornos já enviados</h4>
                  <ul className="retorno-envios">
                    {envios.map((e) => (
                      <li key={e.id} className="retorno-envio">
                        <div className="retorno-envio-cab">
                          <span className={`pill ${e.tipo === "positivo" ? "pill-ok" : "pill-neutral"}`}>
                            {ROTULO_TIPO[e.tipo]}
                          </span>
                          <strong>{quando(e.enviado_em)}</strong>
                          <span className="muted">
                            {[
                              e.enviado_por ? `por ${e.enviado_por}` : null,
                              e.telefone ? formatarTelefone(e.telefone) : "sem número (conversa escolhida no WhatsApp)",
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                        <details>
                          <summary>Ver a mensagem</summary>
                          <p className="retorno-envio-texto">{e.mensagem}</p>
                        </details>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
